import { ClientViewingContext } from "@/shared/domain/agentThread/AgentThreadRecord";
import { panelCaption } from "@/shared/domain/boards/panelCaption";
import { pluralize } from "@/shared/text";

/**
 * Whether the context names no datasets, files, collections, devices, device
 * types, sessions, or views, and carries no table state, visualizer state, or
 * board.
 */
export function isEmptyContext(ctx: ClientViewingContext): boolean {
  return (
    ctx.dataset_ids.length === 0 &&
    ctx.file_ids.length === 0 &&
    ctx.collection_ids.length === 0 &&
    ctx.device_ids.length === 0 &&
    ctx.device_type_ids.length === 0 &&
    ctx.session_ids.length === 0 &&
    ctx.view_ids.length === 0 &&
    (ctx.table_state === undefined || ctx.table_state === null) &&
    (ctx.visualizer_state === undefined || ctx.visualizer_state === null) &&
    boardInContext(ctx) === undefined
  );
}

/** The id and title of the board a context names under `misc_context.board`. */
export interface BoardInContext {
  id: string;
  title?: string;
}

/**
 * The board on screen when the context was taken, or undefined when the
 * context names none. `misc_context.board` also carries the board's state
 * (time range, sections, stages, panels, controls and selection clauses),
 * which this leaves out. A context read back from a sent message arrives as
 * parsed JSON, so the shape is checked rather than assumed.
 */
export function boardInContext(
  ctx: ClientViewingContext,
): BoardInContext | undefined {
  const board = ctx.misc_context?.board;
  if (typeof board !== "object" || board === null || Array.isArray(board)) {
    return undefined;
  }
  const { id, title } = board as Record<string, unknown>;
  if (typeof id !== "string") {
    return undefined;
  }
  return { id, ...(typeof title === "string" && { title }) };
}

/**
 * The board's title, or its id when it has none: how the chat input and the
 * thread name the board to the user.
 */
export function boardLabel(board: BoardInContext): string {
  return board.title ?? board.id;
}

/** One section or panel the author had selected on the board's canvas. */
export type CanvasItemInContext = {
  /** Names the item among the others, for `withoutCanvasItem`. */
  key: string;
  /** A section's title, else its id; a panel's heading on the canvas: its
   *  title, else its place on the board ("Panel 3"). */
  label: string;
} & (
  | { kind: "section" }
  | {
      kind: "panel";
      /** The panel's kind, such as `chart`; `unknown` when the entry names none. */
      panelKind: string;
    }
);

/**
 * The sections, then the panels, that `misc_context.board.selected_on_canvas`
 * lists, each list in the order it holds them; empty when the context names
 * no board or nothing selected on its canvas. The board's members are an
 * untyped record (their shape belongs to the Boards feature), so each entry's
 * shape is checked.
 */
export function canvasItemsInContext(
  ctx: ClientViewingContext,
): CanvasItemInContext[] {
  const board = ctx.misc_context?.board;
  if (!isRecord(board) || !isRecord(board.selected_on_canvas)) {
    return [];
  }
  const selected = board.selected_on_canvas;
  const sections = listMember(selected, "sections").flatMap(
    (entry): CanvasItemInContext[] => {
      const { id, title } = entry;
      return typeof id === "string"
        ? [
            {
              key: canvasSectionKey(id),
              kind: "section",
              label: typeof title === "string" ? title : id,
            },
          ]
        : [];
    },
  );
  const panels = listMember(selected, "panels").flatMap(
    (entry): CanvasItemInContext[] => {
      const { title, kind, position } = entry;
      if (typeof position !== "number") {
        return [];
      }
      return [
        {
          key: canvasPanelKey(position),
          kind: "panel",
          panelKind: typeof kind === "string" ? kind : "unknown",
          label: panelCaption(title, position),
        },
      ];
    },
  );
  return [...sections, ...panels];
}

/**
 * `misc_context` without the selected section or panel `key` names
 * (`CanvasItemInContext.key`), and without `selected_on_canvas` once both
 * of its lists are empty and it has no `sections_omitted` or
 * `panels_omitted` count; every other member is kept.
 */
export function withoutCanvasItem(
  miscContext: ClientViewingContext["misc_context"],
  key: string,
): ClientViewingContext["misc_context"] {
  const board = miscContext?.board;
  if (!isRecord(board)) {
    return miscContext;
  }
  const { selected_on_canvas: selected, ...boardWithoutSelected } = board;
  if (!isRecord(selected)) {
    return miscContext;
  }
  const sections = listMember(selected, "sections").filter(
    (entry) =>
      typeof entry.id !== "string" || canvasSectionKey(entry.id) !== key,
  );
  const panels = listMember(selected, "panels").filter(
    (entry) =>
      typeof entry.position !== "number" ||
      canvasPanelKey(entry.position) !== key,
  );
  // The member goes only when nothing is left in it, including the counts
  // of what its lists left out, so the model is never told that nothing is
  // selected while something is.
  const nothingLeft =
    sections.length === 0 &&
    panels.length === 0 &&
    selected.sections_omitted === undefined &&
    selected.panels_omitted === undefined;
  return {
    ...miscContext,
    board: nothingLeft
      ? boardWithoutSelected
      : { ...board, selected_on_canvas: { ...selected, sections, panels } },
  };
}

/** The object entries of a list member, or none when it is not a list. */
function listMember(
  record: Record<string, unknown>,
  name: string,
): Record<string, unknown>[] {
  const list = record[name];
  return Array.isArray(list) ? list.filter(isRecord) : [];
}

function canvasSectionKey(id: string): string {
  return `section:${id}`;
}

/** A selected panel's key, by its position on the board, which every
 *  selected panel has whether or not it has an id. */
function canvasPanelKey(position: number): string {
  return `panel:${position}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Extract the number of panels from a visualizer state object, or 0
 * if the structure is unexpected.
 */
export function getVisualizerPanelCount(
  state: Record<string, unknown>,
): number {
  if (
    typeof state === "object" &&
    state !== null &&
    "panels" in state &&
    typeof state.panels === "object" &&
    state.panels !== null
  ) {
    return Object.keys(state.panels).length;
  }
  return 0;
}

/**
 * Build a short human-readable summary of a context, e.g.
 * "Context: 2 datasets, 1 file, visualizer" or "Context cleared".
 */
export function buildContextSummary(ctx: ClientViewingContext): string {
  const parts: string[] = [];

  if (ctx.dataset_ids.length > 0) {
    const n = ctx.dataset_ids.length;
    parts.push(`${n} ${pluralize("dataset", n)}`);
  }
  if (ctx.file_ids.length > 0) {
    const n = ctx.file_ids.length;
    parts.push(`${n} ${pluralize("file", n)}`);
  }
  if (ctx.collection_ids.length > 0) {
    const n = ctx.collection_ids.length;
    parts.push(`${n} ${pluralize("collection", n)}`);
  }
  if (ctx.device_ids.length > 0) {
    const n = ctx.device_ids.length;
    parts.push(`${n} ${pluralize("device", n)}`);
  }
  if (ctx.device_type_ids.length > 0) {
    const n = ctx.device_type_ids.length;
    parts.push(`${n} ${pluralize("device type", n)}`);
  }
  if (ctx.session_ids.length > 0) {
    const n = ctx.session_ids.length;
    parts.push(`${n} ${pluralize("session", n)}`);
  }
  // A loaded View stands for its table; without one the table stands alone.
  if (ctx.view_ids.length > 0) {
    const n = ctx.view_ids.length;
    parts.push(`${n} ${pluralize("view", n)}`);
  } else if (ctx.table_state) {
    parts.push(describeTableState(ctx.table_state));
  }
  if (ctx.visualizer_state) {
    parts.push("visualizer");
  }
  if (boardInContext(ctx) !== undefined) {
    parts.push("board");
  }

  if (parts.length === 0) {
    return "Context cleared";
  }

  return `Context: ${parts.join(", ")}`;
}

/**
 * How a resource table with no View loaded reads in the chat: "All datasets"
 * when it shows every row, "Filtered datasets" once any filter narrows it.
 *
 * Named for what the user sees rather than for the table ("datasets table"),
 * which read like the name of a View. A loaded View is labeled by its own name
 * instead, so this is only the no-View case.
 */
export function describeTableState(
  tableState: NonNullable<ClientViewingContext["table_state"]>,
): string {
  const { target, definition } = tableState;
  const filtered =
    definition.roboql !== null ||
    (definition.filters !== null && definition.filters.filters.length > 0);
  return `${filtered ? "Filtered" : "All"} ${target}`;
}
